import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Alert,
  Card,
  CardContent,
  Fade,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Divider,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Business as BusinessIcon,
  People as PeopleIcon,
  Science as ScienceIcon,
  Tune as TuneIcon,
  LocalHospital as LocalHospitalIcon,
  Inventory as InventoryIcon,
  PrecisionManufacturing as PrecisionManufacturingIcon,
  Straighten as StraightenIcon,
  MedicalServices as MedicalServicesIcon,
  Verified as VerifiedIcon,
  Domain as DomainIcon,
  Description as DescriptionIcon,
  Menu as MenuIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { LoaderWrapper } from './index';
import { getApplicationForm, saveApplicationForm, submitApplication } from '../services/api';

// Import form section components
import OrganizationSection from '../pages/Medical/applicationForm/OrganizationSection';
import PersonnelSection from '../pages/Medical/applicationForm/PersonnelSection';
import ScopeSection from '../pages/Medical/applicationForm/ScopeSection';
import EquipmentSection from '../pages/Medical/applicationForm/EquipmentSection';
import InfrastructureSection from '../pages/Medical/applicationForm/InfrastructureSection';
import DocumentationSection from '../pages/Medical/applicationForm/DocumentationSection';

const iconMap = {
  BusinessIcon: BusinessIcon,
  PeopleIcon: PeopleIcon,
  ScienceIcon: ScienceIcon,
  TuneIcon: TuneIcon,
  LocalHospitalIcon: LocalHospitalIcon,
  InventoryIcon: InventoryIcon,
  PrecisionManufacturingIcon: PrecisionManufacturingIcon,
  StraightenIcon: StraightenIcon,
  MedicalServicesIcon: MedicalServicesIcon,
  VerifiedIcon: VerifiedIcon,
  DomainIcon: DomainIcon,
  DescriptionIcon: DescriptionIcon,
};

const ApplicationFormPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [formStructure, setFormStructure] = useState(null);
  const [formData, setFormData] = useState({});
  const [labTypeId, setLabTypeId] = useState(null);
  const [orgId, setOrgId] = useState(null);
  const [autoSaveProgress, setAutoSaveProgress] = useState(0);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  // Get lab type mapping
  const getLabTypeName = (typeId) => {
    const types = {
      '1': 'Testing Laboratory',
      '2': 'Calibration Laboratory', 
      '3': 'Medical Laboratory',
      '5': 'Reference Material Producer'
    };
    return types[typeId.toString()] || 'Laboratory';
  };

  useEffect(() => {
    initializeForm();
  }, []);

  // Auto-save every 30 seconds
  useEffect(() => {
    if (formStructure && formData && Object.keys(formData).length > 0) {
      const interval = setInterval(() => {
        autoSave();
      }, 30000);

      return () => clearInterval(interval);
    }
  }, [formData, formStructure, activeStep]);

  const initializeForm = async () => {
    try {
      // Get parameters from URL query
      const queryParams = new URLSearchParams(location.search);
      const urlLabTypeId = queryParams.get('labTypeId');
      const storedOrgId = localStorage.getItem('orgId') || sessionStorage.getItem('orgId');

      if (!urlLabTypeId || !storedOrgId) {
        toast.error('Missing required parameters. Redirecting to declaration page.');
        navigate('/declaration');
        return;
      }

      setLabTypeId(urlLabTypeId);
      setOrgId(storedOrgId);

      // Fetch form structure and existing data
      const response = await getApplicationForm(urlLabTypeId, storedOrgId);
      
      if (response.data.success) {
        setFormStructure(response.data.formStructure);
        setFormData(response.data.existingData || {});
      } else {
        toast.error('Failed to load application form');
      }

    } catch (error) {
      console.error('Error initializing form:', error);
      toast.error('Failed to initialize application form');
    } finally {
      setLoading(false);
    }
  };

  const autoSave = async () => {
    if (!formStructure || !formData || saving || submitting) return;

    try {
      setAutoSaveProgress(25);
      const currentSection = formStructure.sections[activeStep];
      
      await saveApplicationForm({
        orgId,
        labTypeId,
        formData,
        currentSection: currentSection.id
      });
      
      setAutoSaveProgress(100);
      setTimeout(() => setAutoSaveProgress(0), 1000);
    } catch (error) {
      console.error('Auto-save failed:', error);
      setAutoSaveProgress(0);
    }
  };

  const handleNext = async () => {
    if (activeStep === formStructure.sections.length - 1) {
      // This is the last step, submit the application
      await handleSubmit();
    } else {
      // Save current section and move to next
      await saveCurrentSection();
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const saveCurrentSection = async () => {
    if (!formStructure || saving) return;

    try {
      setSaving(true);
      const currentSection = formStructure.sections[activeStep];
      
      await saveApplicationForm({
        orgId,
        labTypeId,
        formData,
        currentSection: currentSection.id
      });
      
      toast.success('Section saved successfully');
    } catch (error) {
      console.error('Error saving section:', error);
      toast.error('Failed to save section');
      throw error;
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      
      // Final save before submission
      await saveCurrentSection();
      
      // Submit the complete application
      const response = await submitApplication({
        orgId,
        labTypeId
      });
      
      if (response.data.success) {
        toast.success('Application submitted successfully!');
        
        // Redirect to dashboard or confirmation page
        setTimeout(() => {
          navigate('/dashboard', {
            state: {
              applicationSubmitted: true,
              applicationId: response.data.applicationId
            }
          });
        }, 2000);
      } else {
        toast.error(response.data.message || 'Failed to submit application');
      }
    } catch (error) {
      console.error('Error submitting application:', error);
      toast.error('Failed to submit application');
    } finally {
      setSubmitting(false);
    }
  };

  const updateFormData = (sectionId, sectionData) => {
    setFormData(prev => ({
      ...prev,
      [sectionId]: sectionData
    }));
  };

  const toggleMobileDrawer = () => {
    setMobileDrawerOpen(!mobileDrawerOpen);
  };

  const handleSectionClick = (index) => {
    setActiveStep(index);
    if (isMobile) {
      setMobileDrawerOpen(false);
    }
  };

  const renderSectionComponent = (section, sectionData) => {
    const commonProps = {
      data: sectionData || {},
      onUpdate: (data) => updateFormData(section.id, data),
      fields: section.fields
    };

    switch (section.id) {
      case 'organization':
        return <OrganizationSection {...commonProps} />;
      case 'personnel':
        return <PersonnelSection {...commonProps} />;
      case 'testing_scope':
      case 'calibration_scope':
      case 'medical_scope':
      case 'rm_scope':
        return <ScopeSection {...commonProps} sectionType={section.id} />;
      case 'testing_equipment':
      case 'reference_standards':
      case 'medical_equipment':
      case 'characterization':
        return <EquipmentSection {...commonProps} sectionType={section.id} />;
      case 'infrastructure':
        return <InfrastructureSection {...commonProps} />;
      case 'documentation':
        return <DocumentationSection {...commonProps} />;
      default:
        return (
          <Alert severity="warning">
            Section component not implemented: {section.id}
          </Alert>
        );
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          mt: 10,
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <LoaderWrapper 
          loading={true} 
          type="page" 
          message="Loading Application Form..." 
        />
      </Box>
    );
  }

  if (!formStructure) {
    return (
      <Container maxWidth="md" sx={{ mt: 12 }}>
        <Alert severity="error">
          Failed to load application form. Please try again.
        </Alert>
      </Container>
    );
  }

  const currentSection = formStructure.sections[activeStep];
  const currentSectionData = formData[currentSection.id] || {};
  const IconComponent = iconMap[currentSection.icon] || BusinessIcon;

  const sidebarWidth = 280;

  const renderSidebarContent = () => (
    <>
      <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#00024c' }}>
              {getLabTypeName(labTypeId)} Application
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Complete your application
            </Typography>
          </Box>
          {isMobile && (
            <IconButton
              onClick={toggleMobileDrawer}
              sx={{ color: '#00024c' }}
            >
              <CloseIcon />
            </IconButton>
          )}
        </Box>
      </Box>

      {/* Laboratory Navigation Items */}
      <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#00024c', mb: 2 }}>
          Laboratory Forms
        </Typography>
        <List sx={{ p: 0 }}>
          {[
            { title: 'Laboratory Details (1)', icon: BusinessIcon, path: '/lab-details', completed: false },
            { title: 'Organization Chart (2)', icon: PeopleIcon, path: '/organization', completed: false },
            { title: 'Proficiency Testing (3)', icon: ScienceIcon, path: '/proficiency-testing', completed: false },
            { title: 'Discipline Details (4)', icon: TuneIcon, path: '/discipline-details', completed: false },
            { title: 'Lab Staff (5)', icon: PeopleIcon, path: '/lab-staff', completed: false },
            { title: 'Management System Document (6)', icon: DescriptionIcon, path: '/management-system', completed: false },
            { title: 'Fees (7)', icon: InventoryIcon, path: '/fees', completed: false },
            { title: 'Enclosure (8)', icon: DescriptionIcon, path: '/enclosure', completed: false },
          ].map((item, index) => (
            <ListItem key={index} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                onClick={() => {
                  console.log(`Navigate to ${item.path}`);
                  if (isMobile) setMobileDrawerOpen(false);
                }}
                sx={{
                  borderRadius: 1,
                  '&:hover': {
                    backgroundColor: '#f5f5f5',
                  },
                  py: 1,
                }}
              >
                <ListItemIcon sx={{ minWidth: 35 }}>
                  <item.icon sx={{ fontSize: 18, color: item.completed ? '#4caf50' : '#666' }} />
                </ListItemIcon>
                <ListItemText
                  primary={item.title}
                  sx={{
                    '& .MuiListItemText-primary': {
                      fontSize: '0.8rem',
                      fontWeight: 500,
                      color: 'text.primary',
                    },
                  }}
                />
                {item.completed && (
                  <VerifiedIcon sx={{ color: '#4caf50', fontSize: 16 }} />
                )}
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>

      {/* Auto-save progress */}
      {autoSaveProgress > 0 && (
        <Box sx={{ p: 2, borderTop: '1px solid #e0e0e0', mt: 'auto' }}>
          <LoaderWrapper 
            loading={true} 
            type="inline" 
            message="Auto-saving..." 
            size="small"
          />
        </Box>
      )}
    </>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', mt: 8 }}>
      {/* Desktop Sidebar */}
      {!isMobile && (
        <Drawer
          variant="permanent"
          sx={{
            width: sidebarWidth,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: sidebarWidth,
              boxSizing: 'border-box',
              top: 64, // Height of the header
              height: 'calc(100vh - 64px)',
              borderRight: '1px solid #e0e0e0',
              backgroundColor: '#fafafa',
            },
          }}
        >
          {renderSidebarContent()}
        </Drawer>
      )}

      {/* Mobile Sidebar */}
      {isMobile && (
        <Drawer
          variant="temporary"
          open={mobileDrawerOpen}
          onClose={toggleMobileDrawer}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            '& .MuiDrawer-paper': {
              width: sidebarWidth,
              boxSizing: 'border-box',
              backgroundColor: '#fafafa',
            },
          }}
        >
          {renderSidebarContent()}
        </Drawer>
      )}

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: isMobile ? 2 : 3,
          width: isMobile ? '100%' : `calc(100% - ${sidebarWidth}px)`,
        }}
      >
        {/* Mobile Menu Button */}
        {isMobile && (
          <Box sx={{ mb: 2 }}>
            <Button
              variant="outlined"
              startIcon={<MenuIcon />}
              onClick={toggleMobileDrawer}
              sx={{
                borderColor: '#1976d2',
                color: '#1976d2',
                '&:hover': {
                  borderColor: '#1565c0',
                  backgroundColor: 'rgba(25, 118, 210, 0.1)',
                },
              }}
            >
              Navigation Menu
            </Button>
          </Box>
        )}

        <Container maxWidth="lg" sx={{ p: 0 }}>
          <Fade in={true} timeout={1000}>
            <Paper
              elevation={2}
              sx={{
                borderRadius: 2,
                minHeight: 'calc(100vh - 160px)',
                p: isMobile ? 2 : 4,
              }}
            >
              {/* Section Header */}
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: 4, 
                pb: 2, 
                borderBottom: '1px solid #e0e0e0',
                flexDirection: isMobile ? 'column' : 'row',
                textAlign: isMobile ? 'center' : 'left'
              }}>
                <IconComponent sx={{ 
                  fontSize: isMobile ? 28 : 32, 
                  mr: isMobile ? 0 : 2, 
                  mb: isMobile ? 1 : 0,
                  color: '#1976d2' 
                }} />
                <Box>
                  <Typography variant={isMobile ? "h5" : "h4"} sx={{ fontWeight: 600, color: '#1976d2' }}>
                    {currentSection.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Step {activeStep + 1} of {formStructure.sections.length}
                  </Typography>
                </Box>
              </Box>

              {/* Section Content */}
              <Box sx={{ mb: 4 }}>
                {renderSectionComponent(currentSection, currentSectionData)}
              </Box>

              {/* Navigation Buttons */}
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                mt: 4, 
                pt: 3, 
                borderTop: '1px solid #e0e0e0',
                flexDirection: isMobile ? 'column' : 'row',
                gap: isMobile ? 2 : 0
              }}>
                <Button
                  variant="outlined"
                  onClick={handleBack}
                  disabled={activeStep === 0 || saving || submitting}
                  sx={{ px: 4, order: isMobile ? 2 : 1 }}
                >
                  Back
                </Button>

                <Box sx={{ 
                  display: 'flex', 
                  gap: 2, 
                  order: isMobile ? 1 : 2,
                  flexDirection: isMobile ? 'column' : 'row'
                }}>
                  <Button
                    variant="outlined"
                    onClick={saveCurrentSection}
                    disabled={saving || submitting}
                    sx={{ px: 4 }}
                  >
                    <LoaderWrapper loading={saving} type="button">
                      {saving ? 'Saving...' : 'Save'}
                    </LoaderWrapper>
                  </Button>

                  <Button
                    variant="contained"
                    onClick={handleNext}
                    disabled={saving || submitting}
                    sx={{ px: 4 }}
                  >
                    <LoaderWrapper loading={submitting} type="button">
                      {submitting ? 'Submitting...' : activeStep === formStructure.sections.length - 1 ? 'Submit Application' : 'Next'}
                    </LoaderWrapper>
                  </Button>
                </Box>
              </Box>
            </Paper>
          </Fade>
        </Container>
      </Box>
    </Box>
  );
};

export default ApplicationFormPage;
